import React, { useEffect, useState } from "react"
import propTypes from "prop-types"
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useBudget2023Context } from "context/budget2023/Budget2023Context"
import Estructura from "./estructura"
import Partida from "./partida"
import { useSequentialModals } from "../hooks/useSequentialModals"

function ChapterCertPendingZeroClose({ parentUpdate, certification }) {
  useEffect(() => {
    if (!parentUpdate || !certification?.chapterCertInProgress) return
    if (certification.getChapterCertApiPendingCount() > 0) return
    certification.setChapterCertInProgress(false)
  }, [parentUpdate, certification])
  return null
}

ChapterCertPendingZeroClose.propTypes = {
  parentUpdate: propTypes.oneOfType([propTypes.number, propTypes.object]),
  certification: propTypes.shape({
    chapterCertInProgress: propTypes.bool,
    getChapterCertApiPendingCount: propTypes.func.isRequired,
    setChapterCertInProgress: propTypes.func.isRequired,
  }).isRequired,
}

const TablaPartida = ({
  certificaciones,
  capitulo,
  partidasToRender,
  budgetSearchFilterActive = false,
  desplazamiento,
  esUnico,
  soyFinal,
  version,
  numeroIndex,
  openModalCreatePartida,
  setOpenModalCreatePartida,
  projectInfo,
  modificado,
  setModificado,
  contadorActualizaciones,
  setContadorActualizaciones,
  alinearPrecio,
  ocultoFinal,
  isChecked,
  setIsChecked,
}) => {
  const rows = partidasToRender ?? capitulo.partidas ?? []
  const [currentPercentageChapter, setCurrentPercentageChapter] = useState(0)
  const [parentUpdate, setParentUpdate] = useState(0)
  const [isFirstTime, setIsFirstTime] = useState(true)
  const [isDraggingPartida, setIsDraggingPartida] = useState(false)
  const sequentialModals = useSequentialModals()
  const { isBudgetClosed, reorderPartWithinChapter, certification } =
    useBudget2023Context()
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  useEffect(() => {
    setIsFirstTime(false)
  }, [currentPercentageChapter])

  useEffect(() => {
    if (!parentUpdate || !sequentialModals) return

    const timer = setTimeout(() => {
      if (
        sequentialModals.queueLength > 0 &&
        !sequentialModals.hasCurrentModal()
      ) {
        sequentialModals.processNext()
      }
    }, 250)

    return () => clearTimeout(timer)
  }, [parentUpdate, sequentialModals])

  const handleDragEnd = async event => {
    const { active, over } = event

    setIsDraggingPartida(false)

    if (
      budgetSearchFilterActive ||
      isBudgetClosed ||
      certificaciones ||
      !over ||
      active.id === over.id
    ) {
      return
    }

    const targetIndex = rows.findIndex(part => part.partidaId === over.id)

    if (targetIndex === -1) {
      return
    }

    await reorderPartWithinChapter({
      partidaId: active.id,
      newPosition: targetIndex,
    })
  }

  return (
    <>
      <Estructura
        certificaciones={certificaciones}
        openModalCreatePartida={openModalCreatePartida}
        setOpenModalCreatePartida={isOpen =>
          setOpenModalCreatePartida(isOpen, capitulo.chapterId)
        }
        capitulo={capitulo}
        version={version}
        projectInfo={projectInfo}
        total={capitulo.total}
        totalFinal={capitulo.capituloInfo.finalClientPrice}
        ocultoFinal={ocultoFinal}
        desplazamiento={desplazamiento}
        esUnico={esUnico}
        soyFinal={soyFinal}
        alinearPrecio={alinearPrecio}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => {
            if (!budgetSearchFilterActive) setIsDraggingPartida(true)
          }}
          onDragCancel={() => setIsDraggingPartida(false)}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={rows.map(partida => partida.partidaId)}
            strategy={verticalListSortingStrategy}
          >
            {rows.map((partida, index) => (
              <Partida
                isFirstTime={isFirstTime}
                setParentUpdate={setParentUpdate}
                parentUpdate={parentUpdate}
                currentPercentageChapter={parseInt(currentPercentageChapter)}
                setCurrentPercentageChapter={setCurrentPercentageChapter}
                certificaciones={certificaciones}
                numeroIndex={numeroIndex}
                key={partida.partidaId}
                indice={index}
                partidaInfo={partida}
                soyFinal={soyFinal}
                projectInfo={projectInfo}
                setModificado={setModificado}
                modificado={modificado}
                ocultoFinal={ocultoFinal}
                contadorActualizaciones={contadorActualizaciones}
                setContadorActualizaciones={setContadorActualizaciones}
                isChecked={isChecked}
                setIsChecked={setIsChecked}
                version={version}
                openModalCreatePartida={openModalCreatePartida}
                setOpenModalCreatePartida={setOpenModalCreatePartida}
                isDraggingPartida={isDraggingPartida}
                budgetSearchFilterActive={budgetSearchFilterActive}
                sequentialModals={sequentialModals}
              />
            ))}
          </SortableContext>
          <ChapterCertPendingZeroClose
            parentUpdate={parentUpdate}
            certification={certification}
          />
        </DndContext>
      </Estructura>
    </>
  )
}

TablaPartida.propTypes = {
  capitulo: propTypes.object.isRequired,
  partidasToRender: propTypes.array,
  budgetSearchFilterActive: propTypes.bool,
  desplazamiento: propTypes.object.isRequired,
  esUnico: propTypes.bool,
  soyFinal: propTypes.bool,
  projectInfo: propTypes.object.isRequired,
  modificado: propTypes.number,
  setModificado: propTypes.func,
  contadorActualizaciones: propTypes.number,
  setContadorActualizaciones: propTypes.func,
  alinearPrecio: propTypes.bool,
  ocultoFinal: propTypes.bool,
  isChecked: propTypes.bool,
  setIsChecked: propTypes.func,
  version: propTypes.object,
  numeroIndex: propTypes.string,
  openModalCreatePartida: propTypes.bool,
  setOpenModalCreatePartida: propTypes.func,
  certificaciones: propTypes.bool,
}
export default TablaPartida
